import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiBase } from '@/lib/apiBase';
import { setFlowOverride, setSessionToken, setSessionId } from '@/lib/flowStorage';
import { readStoredUtm } from '@/lib/utmCapture';
import { trackEvent } from '@/lib/trackEvent';
import PageSeo from '@/seo/PageSeo';
import { getAssessmentIntro } from '@/data/assessmentPack';
import BrandFormChrome from '@/components/BrandFormChrome';
import {
  brandFormAccentBar,
  brandFormAmbient,
  brandFormCard,
  brandFormFooterBand,
  brandFormHeaderBand,
  brandFormInput,
  brandFormLabelInline,
  brandFormMesh,
  brandFormPage,
  brandFormPrimaryCta,
  brandFormSectionKicker,
} from '@/lib/brandFormShell';

const API_BASE = getApiBase();

export default function StartPage() {
  const navigate = useNavigate();
  const assessmentIntro = useMemo(() => getAssessmentIntro('en'), []);
  const [name, setName] = useState('');
  const [classGrade, setClassGrade] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [institution, setInstitution] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const formStartedRef = useRef(false);
  const onStartFormInteract = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    void trackEvent('form_started', { meta: { surface: 'start', flow: 'direct' } });
  };

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          class: classGrade.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          city: city.trim() || undefined,
          institution: institution.trim() || undefined,
          consentPersonalization: true,
          utm: readStoredUtm(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not save (${res.status})`);
      const sid = data.sessionId;
      const tok = data.sessionToken;
      if (!sid || !tok) throw new Error('Invalid response from server.');
      setSessionId(sid);
      setSessionToken(tok);
      setFlowOverride('direct');
      /* `form_submitted` is recorded server-side in POST /api/users/create (logFunnelEvent lead_created). */
      navigate(`/payment?token=${encodeURIComponent(tok)}`);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={brandFormPage}>
      <PageSeo title="Know your Career DNA" description="Enter your details, then continue to secure payment." path="/start" />
      <div className={brandFormMesh} aria-hidden />
      <div className={brandFormAmbient} aria-hidden />

      <BrandFormChrome />
      <div className="relative z-10 mx-auto max-w-lg scroll-mt-28 pt-0 sm:pt-0">
        <div className={brandFormCard}>
          <div className={brandFormAccentBar} aria-hidden />
          <div className={`${brandFormHeaderBand} text-center sm:text-left`}>
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-900 shadow-sm">
              <Sparkles className="h-3 w-3 text-blue-600" aria-hidden />
              {assessmentIntro.kicker}
            </div>
            <h1 className="mt-3 font-display text-xl font-semibold leading-snug tracking-tight text-gray-900 sm:text-2xl">
              Know your Career DNA
            </h1>
            <p className="mx-auto mt-2 max-w-prose text-[13px] leading-relaxed text-gray-600 sm:mx-0 sm:text-sm">
              {assessmentIntro.body}
            </p>
            <div className="mx-auto mt-3 flex max-w-md flex-wrap justify-center gap-2 sm:mx-0 sm:justify-start">
              {assessmentIntro.bullets.map((b) => (
                <span
                  key={b}
                  className="rounded-full border border-blue-100 bg-blue-50/80 px-2.5 py-1 text-[11px] leading-tight text-gray-700"
                >
                  {b}
                </span>
              ))}
            </div>
            {/* <p className="mt-3 text-center text-[11px] leading-relaxed text-teal-800 sm:text-left">
              Next: secure checkout (plans from ₹1299), then your full Career DNA assessment in one calm sitting.
            </p> */}
          </div>

          <form
            onSubmit={onSubmit}
            onFocusCapture={onStartFormInteract}
            className="space-y-4 bg-gradient-to-b from-white to-sky-50/15 px-5 py-5 sm:px-6 sm:py-6"
          >
            <p className={brandFormSectionKicker}>Your details</p>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="sp-name" className={brandFormLabelInline}>
                  Full name <span className="text-red-600">*</span>
                </label>
                <input
                  id="sp-name"
                  className={brandFormInput}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="As you’d like it on your report"
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sp-class" className={brandFormLabelInline}>
                  Class / grade <span className="text-red-600">*</span>
                </label>
                <input
                  id="sp-class"
                  className={brandFormInput}
                  value={classGrade}
                  onChange={(e) => setClassGrade(e.target.value)}
                  placeholder="e.g. Class 10, 11 science"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sp-phone" className={brandFormLabelInline}>
                  Phone (WhatsApp) <span className="text-red-600">*</span>
                </label>
                <input
                  id="sp-phone"
                  type="tel"
                  className={brandFormInput}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit mobile"
                  autoComplete="tel"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sp-email" className={brandFormLabelInline}>
                  Email <span className="font-normal lowercase text-gray-400">(optional)</span>
                </label>
                <input
                  id="sp-email"
                  type="email"
                  className={brandFormInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="For receipt & report delivery"
                  autoComplete="email"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sp-city" className={brandFormLabelInline}>
                  City / town <span className="font-normal lowercase text-gray-400">(optional)</span>
                </label>
                <input
                  id="sp-city"
                  className={brandFormInput}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Where you’re based"
                  autoComplete="address-level2"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="sp-inst" className={brandFormLabelInline}>
                  School / institution <span className="font-normal lowercase text-gray-400">(optional)</span>
                </label>
                <input
                  id="sp-inst"
                  className={brandFormInput}
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  placeholder="Current school or coaching name"
                  autoComplete="organization"
                />
              </div>
            </div>

            <p className="rounded-xl border border-sky-100/80 bg-gradient-to-br from-sky-50/60 to-teal-50/40 px-3.5 py-3 text-[11px] leading-relaxed text-slate-700">
              By continuing to payment, you agree to our{' '}
              <Link to="/privacy-policy" className="font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms-of-service" className="font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline">
                Terms of Service
              </Link>
              , and to the collection and use of the details above for this assessment, checkout, and related messages.
            </p>

            {err ? (
              <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-sm text-amber-900">{err}</p>
            ) : null}

            <Button type="submit" disabled={busy} className={brandFormPrimaryCta}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'See your career options →'
          
              )}
            </Button>
          </form>

          <div className={brandFormFooterBand}>
            <Link to="/" className="text-sm font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
